import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Reconnect delay in ms (exponential back-off capped at 30s)
const RECONNECT_DELAYS = [1000, 2000, 5000, 10000, 30000];

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated, apiClient } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [toasts, setToasts] = useState([]); // { id, text, notificationId }
  const socketRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const mountedRef = useRef(true);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const count = await apiClient.getInAppUnreadCount();
      setUnreadCount(count || 0);
    } catch {
      // silently ignore
    }
  }, [isAuthenticated, apiClient]);

  // Initial load of unread count
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
    } else {
      setUnreadCount(0);
    }
  }, [isAuthenticated, fetchUnreadCount]);

  const connect = useCallback(() => {
    if (!mountedRef.current || !isAuthenticated) return;

    // Close any existing socket
    if (socketRef.current) {
      socketRef.current.onclose = null;
      socketRef.current.close();
      socketRef.current = null;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // Always connect to the same host:port the page was served from.
    // In development the CRA proxy (setupProxy.js) forwards /ws/notifications to the .NET backend.
    const url = `${protocol}//${window.location.host}/ws/notifications`;

    let ws;
    try {
      ws = new WebSocket(url);
    } catch {
      scheduleReconnect();
      return;
    }

    socketRef.current = ws;

    ws.onopen = () => {
      reconnectAttemptRef.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.Type === 'NewNotification') {
          // Bump unread count
          setUnreadCount(prev => prev + 1);
          // Add toast
          const toastId = Date.now() + Math.random();
          setToasts(prev => [...prev, {
            id: toastId,
            notificationId: msg.NotificationId,
            text: msg.RenderedText,
          }]);
        }
      } catch {
        // ignore malformed messages
      }
    };

    ws.onerror = () => {
      // will be followed by onclose
    };

    ws.onclose = () => {
      socketRef.current = null;
      if (mountedRef.current && isAuthenticated) {
        scheduleReconnect();
      }
    };
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return;
    const delay = RECONNECT_DELAYS[Math.min(reconnectAttemptRef.current, RECONNECT_DELAYS.length - 1)];
    reconnectAttemptRef.current += 1;
    reconnectTimerRef.current = setTimeout(() => {
      if (mountedRef.current) connect();
    }, delay);
  }, [connect]);

  useEffect(() => {
    mountedRef.current = true;

    if (isAuthenticated) {
      connect();
    } else {
      // Disconnect if logged out
      if (socketRef.current) {
        socketRef.current.onclose = null;
        socketRef.current.close();
        socketRef.current = null;
      }
      clearTimeout(reconnectTimerRef.current);
      reconnectAttemptRef.current = 0;
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.onclose = null;
        socketRef.current.close();
        socketRef.current = null;
      }
      clearTimeout(reconnectTimerRef.current);
    };
  }, [isAuthenticated, connect]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const value = {
    unreadCount,
    setUnreadCount,
    fetchUnreadCount,
    toasts,
    dismissToast,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
